import { c as _c } from "react/compiler-runtime";
import { useChatMessages } from '@ai-sdk-tools/store';
import type { ChatOnFinishCallback } from 'ai';
import { useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { useNovaChat } from '../hooks/use-nova-chat';
import type { UIMessageWithMetadata } from '../lib/types';
interface ChatRootProps {
  children: ReactNode;
}

/**
 * Root component that sets up the chat context using @ai-sdk-tools/store.
 * This component initializes the chat hook and makes chat state available
 * to all child components via context.
 *
 * Reads chatUuid, initialMessages, and callbacks from context.
 * Syncs hasAnyMessages from AI SDK store to ChatOverlayContext so NovaProvider
 * can use it without needing access to the AI SDK Provider.
 *
 * @example
 * ```tsx
 * <ChatRoot>
 *   <ChatHeader>...</ChatHeader>
 *   <Messages />
 *   <ChatInput />
 * </ChatRoot>
 * ```
 */
export function ChatRoot(t0) {
  const $ = _c(21);
  const {
    children
  } = t0;
  const {
    chatUuid,
    initialMessages,
    callbacks,
    setHasAnyMessages,
    notificationController,
    isVisible,
    isStandaloneMode
  } = useChatOverlayContext();
  const messages = useChatMessages();
  const currentChatUuidRef = useRef(null);
  const isChatVisibleRef = useRef(null);
  const chatTitleRef = useRef("New Chat");
  let t1;
  let t2;
  if ($[0] !== chatUuid || $[1] !== isVisible) {
    t1 = () => {
      currentChatUuidRef.current = chatUuid;
      isChatVisibleRef.current = isVisible;
    };
    t2 = [chatUuid, isVisible];
    $[0] = chatUuid;
    $[1] = isVisible;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== messages) {
    t3 = () => {
      if (messages.length > 0) {
        const lastUserMessage = [...messages].reverse().find(_temp);
        const title = lastUserMessage?.parts?.[0]?.type === "text" && "text" in lastUserMessage.parts[0] ? String(lastUserMessage.parts[0].text).slice(0, 50) || "New Chat" : "New Chat";
        chatTitleRef.current = title;
      }
    };
    t4 = [messages];
    $[4] = messages;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== callbacks || $[8] !== chatUuid || $[9] !== isStandaloneMode || $[10] !== notificationController) {
    t5 = async () => {
      await notificationController.onStreamFinish({
        chatUuid,
        isChatVisible: isChatVisibleRef.current ?? undefined,
        isStandaloneMode,
        currentChatUuid: currentChatUuidRef.current ?? undefined
      });
      callbacks?.onFinish?.();
    };
    $[7] = callbacks;
    $[8] = chatUuid;
    $[9] = isStandaloneMode;
    $[10] = notificationController;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleFinish = t5;
  const t6 = callbacks?.onError;
  let t7;
  if ($[12] !== chatUuid || $[13] !== handleFinish || $[14] !== initialMessages || $[15] !== t6) {
    t7 = {
      chatUuid,
      initialMessages,
      onFinish: handleFinish,
      onError: t6
    };
    $[12] = chatUuid;
    $[13] = handleFinish;
    $[14] = initialMessages;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  useNovaChat(t7);
  let t8;
  let t9;
  if ($[17] !== messages.length || $[18] !== setHasAnyMessages) {
    t8 = () => {
      setHasAnyMessages(messages.length > 0);
    };
    t9 = [messages.length, setHasAnyMessages];
    $[17] = messages.length;
    $[18] = setHasAnyMessages;
    $[19] = t8;
    $[20] = t9;
  } else {
    t8 = $[19];
    t9 = $[20];
  }
  useEffect(t8, t9);
  return children;
}
function _temp(msg) {
  return msg.role === "user";
}